/**
 * src/components/MapSceneController.tsx
 *
 * Headless controller for the main map's 3D "scene" settings, inspired by
 * GeoLibre's globe/terrain/atmosphere toggles (which it gets from CesiumJS —
 * we get the equivalents natively from Mapbox GL v3):
 *
 *   - Globe projection        → map.setProjection('globe' | 'mercator')
 *   - Atmosphere / sky (fog)  → map.setFog(<preset> | null)
 *   - 3D terrain + exaggeration → mapbox-dem raster-dem + map.setTerrain(...)
 *
 * Renders nothing. It applies the current settings whenever they change AND
 * re-applies them on `style.load`, because swapping the basemap style wipes
 * fog / terrain / projection. This keeps the scene stable across the Settings
 * overlay's style switches without threading the logic through App.tsx's
 * (very large) map-init path.
 */

import { useEffect } from 'react';

/** Terrain DEM source id (shared with SettingsOverlay's legacy path). */
const DEM_SOURCE_ID = 'mapbox-dem';

/** Classic Mapbox globe atmosphere — matches our mini-globe / preview maps. */
const ATMOSPHERE_FOG = {
  color: 'rgb(186, 210, 235)',        // lower atmosphere
  'high-color': 'rgb(36, 92, 223)',   // upper atmosphere
  'horizon-blend': 0.02,
  'space-color': 'rgb(11, 11, 25)',   // space behind the globe
  'star-intensity': 0.6,
} as const;

export interface MapSceneControllerProps {
  map: mapboxgl.Map | null;
  /** Master 3D gate (pitch/terrain only make sense when 3D is enabled). */
  enable3D: boolean;
  /** Render the map as a 3D globe instead of a flat Mercator plane. */
  showGlobe: boolean;
  /** Draw the atmospheric sky/fog halo. */
  showAtmosphere: boolean;
  /** Drape the basemap over real elevation. */
  showTerrain: boolean;
  /** Vertical terrain exaggeration multiplier (1 = true scale). */
  terrainExaggeration: number;
  /**
   * Current basemap style URL. Passed so the scene re-applies after the user
   * switches styles — `setStyle` resets projection/fog/terrain to the new
   * style's defaults, which would otherwise flatten the globe.
   */
  mapStyle?: string;
}

const MapSceneController: React.FC<MapSceneControllerProps> = ({
  map,
  enable3D,
  showGlobe,
  showAtmosphere,
  showTerrain,
  terrainExaggeration,
  mapStyle,
}) => {
  useEffect(() => {
    if (!map) return;

    const m = map as mapboxgl.Map & {
      setProjection?: (p: string | { name: string }) => void;
    };

    const applyScene = () => {
      // ── Projection (globe vs mercator) ──
      try {
        m.setProjection?.(showGlobe ? 'globe' : 'mercator');
      } catch { /* older GL / unsupported — ignore */ }

      // ── Atmosphere / sky ──
      try {
        // Fog reads best on the globe or at pitch; harmless in flat view.
        m.setFog(showAtmosphere ? { ...ATMOSPHERE_FOG } : null);
      } catch { /* ignore */ }

      // ── Terrain ──
      try {
        if (showTerrain && enable3D) {
          if (!m.getSource(DEM_SOURCE_ID)) {
            m.addSource(DEM_SOURCE_ID, {
              type: 'raster-dem',
              url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
              tileSize: 512,
              maxzoom: 14,
            });
          }
          const exaggeration = Number.isFinite(terrainExaggeration) && terrainExaggeration > 0
            ? terrainExaggeration
            : 1;
          m.setTerrain({ source: DEM_SOURCE_ID, exaggeration });
        } else {
          m.setTerrain(null);
        }
      } catch { /* ignore */ }
    };

    // `setStyle` can reset projection to the new style's default (mercator)
    // *after* `style.load` fires — especially on a full "rebuild from
    // scratch" (e.g. switching to dark-v11), where Mapbox keeps settling
    // sprite/sources/projection for several frames past `style.load`. A
    // single rAF can lose that race and flatten the globe. So we re-assert
    // the scene multiple times: immediately, on the next frame, and once
    // more when the map goes `idle` (style fully settled).
    let raf = 0;
    const safeApply = () => { try { applyScene(); } catch { /* ignore */ } };
    const applySceneRobust = () => {
      safeApply();
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(safeApply);
      // `idle` fires once the style is fully loaded and the map is settled;
      // re-asserting here is the definitive win against the style default.
      try { m.once('idle', safeApply); } catch { /* ignore */ }
    };

    // Apply now if the style is ready; otherwise the `style.load` listener
    // below fires when the current style finishes loading.
    if (m.isStyleLoaded()) applySceneRobust();

    // Re-apply after every basemap style swap and on style load.
    //
    // IMPORTANT: `setStyle` does NOT always fire `style.load`. When Mapbox can
    // diff the old→new style it emits only `styledata`; it emits `style.load`
    // only on a full "rebuild from scratch". Relying on `style.load` alone
    // therefore misses the diff case and the globe stays flat. This effect
    // re-runs the instant `mapStyle` changes (right after SettingsOverlay
    // called `setStyle`), so we also arm a one-shot `idle` re-assert here —
    // `idle` fires once the new style has fully settled regardless of which
    // path Mapbox took, making it the reliable place to win the projection.
    m.on('style.load', applySceneRobust);
    m.once('idle', applySceneRobust);
    return () => {
      cancelAnimationFrame(raf);
      try { m.off('style.load', applySceneRobust); } catch { /* ignore */ }
      try { m.off('idle', applySceneRobust); } catch { /* ignore */ }
      try { m.off('idle', safeApply); } catch { /* ignore */ }
    };
  }, [map, enable3D, showGlobe, showAtmosphere, showTerrain, terrainExaggeration, mapStyle]);

  return null;
};

export default MapSceneController;
